import React from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import { observer } from 'mobx-react';
import { useHistory } from 'react-router-dom';
import { isFarmBasedPolicyType } from '@ourbranch/farm-utils';

import { useStore } from 'core/store';
import { Loading } from 'core/components/loading';
import { Label } from 'core/components/label';
import { Button } from 'core/components/button';
import { Card } from 'core/components/card';
import documentNotFoundIcon from 'core/assets/svg/imgDocumentNotFound.svg';
import { GET_POLICY_FROM_VERSION } from '../revert-version-modal/revert-version.queries';
import PreviewClarionDoorRequestDifference from 'customer/components/policy/preview-clariondoor-request-diff/preview-clariondoor-request-diff';
import { getSegmentDetailsForCdRequest } from 'customer/components/policy/preview-clariondoor-request-diff/cd-vehicle-labels';
import { getClarionDoorIdFromSnapshot } from './helpers';
import { useStyles } from './version-compare.styles';

const padVersion = (version) => `00${version}`.substr(-3);

/**
 * Resolves the previous version number for `targetVersion` from the policy's version history:
 * the largest version strictly less than the target. Falls back to `targetVersion - 1` if the
 * history isn't available (e.g. deep-linked before the policy loaded).
 */
const getPreviousVersion = (versionHistory, targetVersion) => {
  const earlier = (versionHistory ?? [])
    .map((v) => v.version)
    .filter((v) => typeof v === 'number' && v < targetVersion);
  if (earlier.length) {
    return Math.max(...earlier);
  }
  return targetVersion - 1;
};

/**
 * In-page empty/error state shown when a comparison can't be rendered (no rating request, no previous
 * version, farm, or a fetch error). Mirrors the document-not-found pattern from design: illustration,
 * green title, explanation, a divider + hint line, and a "Back to policy" button so the operator isn't
 * stranded on a dead-end page.
 */
const Message = ({ classes, accountId, policyId, header, detail, hint, onBack }) => (
  <div className={classes.messageContainer}>
    <Card type="primary" className={classes.messageCard}>
      <div className={classes.messageContent}>
        <img src={documentNotFoundIcon} alt="" className={classes.messageIcon} />
        <div className={classes.messageText}>
          <Label type="subtitle" className={classes.messageHeader}>
            {header}
          </Label>
          <Label type="infoValue" className={classes.messageBody}>
            {detail}
          </Label>
        </div>
        {hint && (
          <>
            <div className={classes.messageDivider} />
            <Label type="infoValue" className={classes.messageHint}>
              {hint}
            </Label>
          </>
        )}
        {accountId && policyId && (
          <Button color="secondary" variant="contained" onClick={onBack} className={classes.backButton}>
            ← Back to policy
          </Button>
        )}
      </div>
    </Card>
  </div>
);

Message.propTypes = {
  classes: PropTypes.object.isRequired,
  accountId: PropTypes.string,
  policyId: PropTypes.string,
  header: PropTypes.string.isRequired,
  detail: PropTypes.node.isRequired,
  hint: PropTypes.node,
  onBack: PropTypes.func
};

/**
 * VersionCompare — staff-only route that compares the ClarionDoor rating request XML of a policy
 * version to its immediate predecessor (GH #29604). It fetches both historical snapshots from the
 * PolicyAudit table (getPolicyFromVersion), extracts each one's ClarionDoor request id, and reuses
 * the existing ClarionDoor diff view via its cdId direct-compare path.
 *
 * Farm policies are excluded for v1 — they store ClarionDoor ids as a per-risk map rather than a
 * scalar, which the version snapshot query doesn't fetch.
 */
const VersionCompare = ({ accountId: accountIdProp, policyId, version }) => {
  const classes = useStyles();
  const history = useHistory();
  const {
    account: {
      policies: { policy: policyStore }
    }
  } = useStore();

  const targetVersion = Number(version);
  const { policyType, versionHistory } = policyStore.policy ?? {};
  const previousVersion = getPreviousVersion(versionHistory, targetVersion);
  // Prefer the route's account id; fall back to deriving it from the policyId prefix.
  const accountId = accountIdProp ?? policyId?.split('-')[0];
  const goBackToPolicy = () => history.push(`/customer/${accountId}/policy/${policyId}`);

  const isFarm = isFarmBasedPolicyType(policyType);
  const hasPreviousVersion = Number.isFinite(targetVersion) && targetVersion > 1 && previousVersion >= 1;

  // Fetch both historical snapshots. Skip when there's nothing to compare (farm / no previous version)
  // so we don't fire pointless queries before rendering the guard message.
  const skip = isFarm || !hasPreviousVersion;
  const currentSnapshot = useQuery(GET_POLICY_FROM_VERSION, {
    variables: { id: policyId, version: String(targetVersion) },
    fetchPolicy: 'no-cache',
    skip
  });
  const previousSnapshot = useQuery(GET_POLICY_FROM_VERSION, {
    variables: { id: policyId, version: String(previousVersion) },
    fetchPolicy: 'no-cache',
    skip
  });

  const sharedProps = { classes, accountId, policyId, onBack: goBackToPolicy };

  // Compute the inner content for each state, then render it once under the shared page title so
  // "Policy version comparison" heads every state (diff, loading, and the empty/error cards).
  const renderContent = () => {
    if (isFarm) {
      return (
        <Message
          {...sharedProps}
          header="Version comparison is not yet supported for farm policies"
          detail="Farm policies store their ClarionDoor rating across multiple per-risk requests, which this comparison doesn't yet support."
        />
      );
    }

    if (!hasPreviousVersion) {
      return (
        <Message
          {...sharedProps}
          header={`Version ${padVersion(targetVersion)} has no previous version to compare`}
          detail="Comparison shows the rating difference between a version and the one before it, so it's only available from version 002 onward."
        />
      );
    }

    if (currentSnapshot.loading || previousSnapshot.loading) {
      return <Loading />;
    }

    const error = currentSnapshot.error || previousSnapshot.error;
    if (error) {
      return <Message {...sharedProps} header="Couldn't load the policy versions to compare" detail={error.message} />;
    }

    const cdIdAfter = getClarionDoorIdFromSnapshot(currentSnapshot.data?.getPolicyFromVersion);
    const cdIdBefore = getClarionDoorIdFromSnapshot(previousSnapshot.data?.getPolicyFromVersion);

    if (!cdIdBefore || !cdIdAfter) {
      const missing = !cdIdAfter ? padVersion(targetVersion) : padVersion(previousVersion);
      return (
        <Message
          {...sharedProps}
          header={`No rating data for version ${missing}`}
          detail="Rating data is only captured when a policy is re-rated. Since this version wasn't re-rated, there's nothing to compare against the previous version."
          hint="In the policy change history, look for a version where the premium changed — those versions will have rating data available."
        />
      );
    }

    return (
      <PreviewClarionDoorRequestDifference
        cdIdBefore={cdIdBefore}
        cdIdAfter={cdIdAfter}
        hideBranchDataDiff
        beforeCars={getSegmentDetailsForCdRequest(previousSnapshot.data?.getPolicyFromVersion, cdIdBefore).cars}
        afterCars={getSegmentDetailsForCdRequest(currentSnapshot.data?.getPolicyFromVersion, cdIdAfter).cars}
        beforeTitle={`ClarionDoor Request - Version ${padVersion(previousVersion)}`}
        afterTitle={`ClarionDoor Request - Version ${padVersion(targetVersion)}`}
      />
    );
  };

  return (
    <div className={classes.page}>
      <Label type="title" className={classes.pageTitle}>
        Policy version comparison
      </Label>
      {renderContent()}
    </div>
  );
};

VersionCompare.propTypes = {
  accountId: PropTypes.string,
  policyId: PropTypes.string.isRequired,
  version: PropTypes.string.isRequired
};

export default observer(VersionCompare);
